<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import IconPickerGrid from './IconPickerGrid.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  folder: { type: Object, default: null },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();

const name = ref('');
const icon = ref('i-lucide-folder');
const category = ref('sales');

watch(
  () => props.folder,
  f => {
    name.value = f?.name ?? '';
    icon.value = f?.icon ?? 'i-lucide-folder';
    category.value = f?.category ?? 'sales';
  },
  { immediate: true }
);

function submit() {
  if (!name.value.trim()) return;
  emit('submit', {
    name: name.value.trim(),
    icon: icon.value,
    category: category.value,
  });
}
</script>

<template>
  <div
    class="fixed inset-0 z-[10010] flex items-center justify-center bg-black/40"
    @click.self="emit('cancel')"
  >
    <div
      class="bg-n-background rounded-xl shadow-2xl w-full max-w-sm mx-4 p-5 flex flex-col gap-4"
    >
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{
          folder
            ? t('KANBAN.FOLDERS.EDIT_TITLE')
            : t('KANBAN.FOLDERS.CREATE_TITLE')
        }}
      </h3>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('KANBAN.FOLDERS.NAME_LABEL') }}
        </label>
        <input
          v-model="name"
          type="text"
          class="text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-surface-2 text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('KANBAN.FOLDERS.NAME_PLACEHOLDER')"
          @keydown.enter.prevent="submit"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('KANBAN.FOLDERS.ICON_LABEL') }}
        </label>
        <IconPickerGrid v-model="icon" />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('KANBAN.FOLDERS.CATEGORY_LABEL') }}
        </label>
        <div
          class="flex items-center gap-2 bg-n-surface-2 p-1 rounded-lg border border-n-weak w-fit"
          :class="{ 'opacity-60 pointer-events-none': !!folder }"
        >
          <button
            class="flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors"
            :class="
              category === 'sales'
                ? 'bg-n-background shadow-sm text-n-slate-12 border border-n-container'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            type="button"
            @click="category = 'sales'"
          >
            <span class="i-lucide-trending-up size-4 text-n-teal-9" />
            {{ t('KANBAN.SETTINGS.CATEGORY_SALES') }}
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors"
            :class="
              category === 'post_sales'
                ? 'bg-n-background shadow-sm text-n-slate-12 border border-n-container'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            type="button"
            @click="category = 'post_sales'"
          >
            <span class="i-lucide-headset size-4 text-n-amber-10" />
            {{ t('KANBAN.SETTINGS.CATEGORY_POST_SALES') }}
          </button>
        </div>
        <span v-if="folder" class="text-[11px] text-n-slate-9">
          {{ t('KANBAN.SETTINGS.CATEGORY_LOCKED') }}
        </span>
      </div>

      <div class="flex justify-end gap-2 pt-1">
        <Button
          faded
          slate
          sm
          :label="t('KANBAN.FOLDERS.CANCEL')"
          type="button"
          @click="emit('cancel')"
        />
        <Button
          sm
          :label="
            folder ? t('KANBAN.FOLDERS.SAVE') : t('KANBAN.FOLDERS.CREATE')
          "
          :disabled="!name.trim()"
          type="button"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>
